import { useAppliedTheme } from '@/components/ThemeProvider/hooks'
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { CheckIcon, CopyIcon, LucideIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Text } from '../Atoms'
import { HStack } from '../Atoms/Layout/HStack'
import { FormattedCode, FormattedCodeProps } from './FormattedCode'

const copyToClipboard = (code: string) => {
  navigator.clipboard.writeText(code)
}

type CodeBlockProps = {
  code: string
  language?: string
  title?: string
  titleIcon?: LucideIcon
  className?: string
  theme?: FormattedCodeProps['theme']
  showLineNumbers?: boolean
  wrapLines?: boolean
}

export function CodeBlock({
  theme = 'muted',
  showLineNumbers = false,
  wrapLines = false,
  ...props
}: CodeBlockProps) {
  const { t } = useTranslation('common')
  const { isDarkTheme } = useAppliedTheme()

  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (copied) {
      setTimeout(() => setCopied(false), 1500)
    }
  }, [copied])

  return (
    <TooltipProvider>
      <div
        className={cn('relative flex flex-col overflow-clip rounded-lg border', props.className)}
      >
        {props.title && (
          <div className="bg-background-level-2 flex items-center justify-between border-b py-2 pl-4 pr-2">
            <HStack gap="sm" align="center" className="text-muted-foreground select-none">
              {props.titleIcon && React.createElement(props.titleIcon, { className: 'size-4' })}
              <Text>{props.title}</Text>
            </HStack>
            <Tooltip open={copied}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    copyToClipboard(props.code)
                    setCopied(true)
                  }}
                  className="text-muted-foreground hover:text-foreground hover:bg-alpha-100 rounded-md p-2 text-sm transition-colors"
                >
                  {copied ? (
                    <CheckIcon className="animate-in fade-in-0 zoom-in-95 size-4" />
                  ) : (
                    <CopyIcon className="animate-in fade-in-0 zoom-in-95 size-4" />
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('actions.copied')}</TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* No `overscroll-contain` here: a block with no height cap of its own never scrolls, and a
            scroll container that cannot scroll plus contain swallows the wheel instead of passing it
            to the dialog or sheet around it (the SAP payload dialog). A capped block still scrolls
            inside itself and hands the wheel on only at its ends, as any scroll container does. */}
        <div
          className={cn(
            'relative flex-1 overflow-y-auto',
            theme === 'muted' || isDarkTheme
              ? 'bg-background-level-2'
              : theme === 'light' || isDarkTheme
                ? 'bg-background-level-1'
                : 'bg-primary',
          )}
        >
          <FormattedCode
            code={props.code}
            language={props.language}
            theme={isDarkTheme ? 'dark' : theme}
            showLineNumbers={showLineNumbers}
            hasTitle={!!props.title}
            wrapLines={wrapLines}
          />

          {!props.title && (
            <Tooltip open={copied}>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon-sm"
                  onClick={() => {
                    copyToClipboard(props.code)
                    setCopied(true)
                  }}
                  className="absolute right-2 top-2"
                >
                  {copied ? (
                    <CheckIcon className="animate-in fade-in-0 zoom-in-95 size-4" />
                  ) : (
                    <CopyIcon className="animate-in fade-in-0 zoom-in-95 size-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('actions.copied')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
